import { z } from "zod";
import { artifactRefSchema } from "./context.js";
import { evidenceLocatorSchema } from "./evidence-locator.js";
/** Intake preserves observations. It cannot certify, approve or publish a rule. */
export const WORKBOOK_INTAKE_MAX_ROWS = 500;
const digest = z.string().regex(/^[a-f0-9]{64}$/);
const workbookLocator = evidenceLocatorSchema.safeExtend({
    kind: z.literal("document"),
    value: z
        .string()
        .regex(/^xlsx:xl\/worksheets\/[A-Za-z0-9_-]+\.xml#(?:row=[1-9]\d{0,6}|cell=[A-Z]{1,3}[1-9]\d{0,6})$/),
});
export const workbookEvidenceRowSchema = z
    .object({
    rowKey: z.string().trim().min(1).max(120),
    kind: z.enum([
        "CLASSIFICATION",
        "PURCHASE_ST",
        "SALE_ST",
        "ROUTE_REFERENCE",
        "COLLECTION_REFERENCE",
        "FISCAL_REFERENCE",
    ]),
    locator: workbookLocator,
    /** Exact normalized source text; no formula execution or numeric coercion. */
    sourceText: z.string().min(1).max(2_000),
})
    .strict();
const intakeBase = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    /** Operator declaration: only the explicitly selected fiscal extract is retained. */
    fiscalExtractConfirmed: z.literal(true),
    sourceEvidenceId: z.string().uuid().optional(),
    sourceContentSha256: digest,
    extractorVersion: z.string().regex(/^[a-z0-9][a-z0-9._-]{0,79}$/),
    rows: z.array(workbookEvidenceRowSchema).min(1).max(WORKBOOK_INTAKE_MAX_ROWS),
    expectedIntakeHash: digest.optional(),
})
    .strict();
export const workbookEvidenceIntakeRequestSchema = z
    .discriminatedUnion("mode", [
    intakeBase.extend({ mode: z.literal("DRY_RUN") }),
    intakeBase.extend({
        mode: z.literal("COMMIT"),
        sourceEvidenceId: z.string().uuid(),
        expectedIntakeHash: digest,
    }),
])
    .superRefine((value, context) => {
    const keys = new Set();
    const locators = new Set();
    for (const [index, row] of value.rows.entries()) {
        const locatorKey = JSON.stringify({ ...row.locator, heading: row.locator.heading ?? null });
        if (keys.has(row.rowKey) || locators.has(locatorKey)) {
            context.addIssue({
                code: "custom",
                path: ["rows", index],
                message: "Duplicate row or locator",
            });
        }
        keys.add(row.rowKey);
        locators.add(locatorKey);
    }
});
export const workbookEvidenceIntakeResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    mode: z.enum(["DRY_RUN", "COMMIT"]),
    intakeId: z.string().uuid().nullable(),
    intakeHash: digest,
    state: z.literal("NEW"),
    executable: z.literal(false),
    sourceEvidenceId: z.string().uuid().nullable(),
    sourceContentSha256: digest,
    extractorVersion: z.string(),
    sourceVerification: z.enum(["NOT_PROVIDED", "VERIFIED_ARTIFACTS"]),
    sourceArtifacts: z
        .object({
        sourceId: z.string().uuid(),
        raw: artifactRefSchema,
        normalized: artifactRefSchema,
    })
        .strict()
        .nullable(),
    persistable: z.boolean(),
    blockers: z.array(z.enum(["SOURCE_EVIDENCE_REQUIRED", "SOURCE_ROWS_INVALID"])),
    requiredReview: z.tuple([
        z.literal("OFFICIAL_EVIDENCE"),
        z.literal("LEGAL_VALIDITY"),
        z.literal("PRODUCT_APPLICABILITY"),
    ]),
    rows: z
        .array(workbookEvidenceRowSchema.extend({
        verification: z.enum(["UNVERIFIED", "VERIFIED_SOURCE_TEXT", "INVALID"]),
        issues: z.array(z.enum(["LOCATOR_NOT_FOUND", "SOURCE_TEXT_MISMATCH"])),
    }))
        .max(WORKBOOK_INTAKE_MAX_ROWS),
    createdAt: z.string().datetime().nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.sourceVerification === "VERIFIED_ARTIFACTS" &&
        (!value.sourceEvidenceId ||
            !value.sourceArtifacts ||
            value.sourceArtifacts.raw.checksumSha256 !== value.sourceContentSha256)) {
        context.addIssue({
            code: "custom",
            path: ["sourceArtifacts"],
            message: "Verified source artifacts and their raw digest are required",
        });
    }
    if (value.persistable &&
        (value.sourceVerification !== "VERIFIED_ARTIFACTS" ||
            value.rows.some((row) => row.verification !== "VERIFIED_SOURCE_TEXT" || row.issues.length > 0))) {
        context.addIssue({
            code: "custom",
            path: ["persistable"],
            message: "Only verified source rows are retainable",
        });
    }
    if (value.intakeId && (!value.persistable || value.mode !== "COMMIT")) {
        context.addIssue({
            code: "custom",
            path: ["intakeId"],
            message: "A stored receipt requires a verified commit",
        });
    }
});
//# sourceMappingURL=workbook-evidence-intake.js.map